// @react-functional
import { useState, useEffect } from 'react';

import {
  getLegislationBreadcrumb,
  getLegislationCodeBreadcrumb,
} from '../page/legislation.api';
import {
  LegislationBreadcrumbList,
  FormattedLegislationBreadcrumb,
} from '../page/legislation.types';

import { buildUrlWithParameters } from '../../utils/url-utils/urlUtils';
import { getDateKey } from '../../utils/date-utils/dateUtils';

const createBreadcrumbLinks = (
  breadcrumb: LegislationBreadcrumbList,
  date: string | Date,
  source: string,
) => {
  const dateKey = getDateKey(date);
  const today = getDateKey(new Date());

  const structuredBreadcrumb = breadcrumb?.map(({ url, title }) => {
    return {
      label: title,
      url: buildUrlWithParameters(url, {
        source,
        ...(dateKey && dateKey !== today && { dateKey }),
      }),
    };
  });

  if (structuredBreadcrumb.length > 3) {
    return [
      structuredBreadcrumb[0],
      {
        label: '...',
        url: '',
        current: true,
        subBreadcrumbs: [
          ...structuredBreadcrumb.slice(1, structuredBreadcrumb.length - 2),
        ],
      },
      ...structuredBreadcrumb.slice(-2),
    ];
  }

  return structuredBreadcrumb;
};
/**
 *
 * @param textId the parent text (a code, decree, etc.)
 * @param id the id of the entity for which we're retrieving breadrcrumbs
 * @param date breadcrumbs only make sense at a given date
 * @param entity entity asked (article or section)
 */
export const useBreadcrumbFetcher = (
  textId: string,
  id: string,
  date: string | Date,
  entity: 'article' | 'section',
) => {
  const [breadcrumb, setBreadcrumb] = useState<
    FormattedLegislationBreadcrumb[] | null
  >(null);

  useEffect(() => {
    const fetchData = async () => {
      const data =
        entity === 'article'
          ? await getLegislationBreadcrumb(textId, id)
          : await getLegislationCodeBreadcrumb(textId, id, date);
      setBreadcrumb(
        createBreadcrumbLinks(data, date, `legislation-${entity}-breadcrumb`),
      );
    };

    fetchData();
  }, [textId, id, date, entity]);

  return breadcrumb;
};
